import { useState, useRef } from 'react';
import './AdminView.css';

const ANTHROPIC_API = 'https://api.anthropic.com/v1/messages';

export default function AdminView({ items, onPublish, onRemove }) {
  const [imageFile, setImageFile] = useState(null);
  const [imageDataUrl, setImageDataUrl] = useState(null);
  const [imageBase64, setImageBase64] = useState(null);
  const [notes, setNotes] = useState('');
  const [listing, setListing] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editTags, setEditTags] = useState('');
  const [askingPrice, setAskingPrice] = useState('');
  const [loading, setLoading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef();

  const handleFile = (file) => {
    if (!file) return;
    setImageFile(file);
    setError('');
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target.result;
      setImageDataUrl(dataUrl);
      setImageBase64(dataUrl.split(',')[1]);
    };
    reader.readAsDataURL(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) handleFile(file);
  };

  const generateListing = async () => {
    if (!imageBase64) { setError('Please upload a photo first.'); return; }
    setLoading(true);
    setError('');
    setListing(null);

    const mediaType = imageFile?.type || 'image/jpeg';

    const prompt = `You are a yard sale listing assistant. Analyze this item carefully and respond ONLY with a valid JSON object — no markdown, no backticks, no extra text. The JSON must have exactly these fields:

- "title": follow this format exactly: "[Brand] [Item type] — [Key spec or distinguishing detail]"
  Examples: "Aero Leather Co. Horsehide Jacket — Men's Size 40", "Weber Kettle Grill — 22 inch, Black", "Canon AE-1 Film Camera — 50mm f/1.8 Lens Included"
  Do not use adjectives like 'vintage', 'classic', 'stunning', 'beautiful'. Just the facts.

- "description": 2-3 sentences. Plain, factual, no sales language. State what the item is, who made it, any relevant specs (size, model, material, age if known), and condition. Do not say things like 'perfect for', 'great addition to', 'any enthusiast will love'. Just describe what is actually there. If you can identify the brand or model from the photo, say so.

- "tags": a comma-separated list of every distinct item, material, brand, or feature visible in the photo. Be thorough — this is used for search. Example: "leather jacket, horsehide, brass zipper, tartan lining, Aero Leather, size 40, brown"

- "estimatedValueLow": estimated resale value low end in USD (integer, no $ sign)
- "estimatedValueHigh": estimated resale value high end in USD (integer, no $ sign)
- "category": one of: Furniture, Electronics, Clothing, Books, Kitchen, Tools, Toys, Art, Sports, Other
${notes ? `\nSeller notes: ${notes}` : ''}

Respond with JSON only.`;

    try {
      const response = await fetch(ANTHROPIC_API, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': process.env.REACT_APP_ANTHROPIC_KEY || '',
          'anthropic-version': '2023-06-01',
          'anthropic-dangerous-direct-browser-access': 'true',
        },
        body: JSON.stringify({
          model: 'claude-sonnet-4-20250514',
          max_tokens: 1000,
          messages: [{
            role: 'user',
            content: [
              { type: 'image', source: { type: 'base64', media_type: mediaType, data: imageBase64 } },
              { type: 'text', text: prompt }
            ]
          }]
        })
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err?.error?.message || `API error ${response.status}`);
      }

      const data = await response.json();
      const raw = data.content.map(b => b.text || '').join('').trim();
      const parsed = JSON.parse(raw.replace(/```json|```/g, '').trim());
      setListing(parsed);
      setEditTitle(parsed.title);
      setEditDescription(parsed.description);
      setEditTags(parsed.tags);
      setAskingPrice(String(parsed.estimatedValueLow));
    } catch (err) {
      setError(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const publish = async () => {
    if (!listing) return;
    setPublishing(true);
    await onPublish({
      title: editTitle,
      description: editDescription,
      tags: editTags,
      category: listing.category,
      estimatedLow: listing.estimatedValueLow,
      estimatedHigh: listing.estimatedValueHigh,
      price: parseFloat(askingPrice) || 0,
      imageFile,
    });
    setPublishing(false);
    // Reset form
    setImageFile(null);
    setImageDataUrl(null);
    setImageBase64(null);
    setNotes('');
    setListing(null);
    setEditTitle('');
    setEditDescription('');
    setEditTags('');
    setAskingPrice('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="admin-wrap">
      <div className="admin-header">
        <h1>Listing manager</h1>
        <p>Upload a photo, add optional notes, and let AI write the listing and estimate the value.</p>
      </div>

      <div className="form-card">
        <h2>New item</h2>

        <div
          className={`drop-zone ${imageDataUrl ? 'has-image' : ''}`}
          onDragOver={e => e.preventDefault()}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={e => handleFile(e.target.files[0])}
          />
          {imageDataUrl ? (
            <img src={imageDataUrl} alt="Preview" className="preview-img" />
          ) : (
            <div className="drop-placeholder">
              <div className="drop-icon">📷</div>
              <strong>Click or drag a photo here</strong>
              <span>JPEG, PNG, WEBP, HEIC</span>
            </div>
          )}
        </div>

        {imageDataUrl && (
          <button className="btn-link" onClick={() => {
            setImageFile(null); setImageDataUrl(null);
            setImageBase64(null); setListing(null);
          }}>
            Remove photo
          </button>
        )}

        <label className="field-label" style={{ marginTop: '1.25rem' }}>
          Notes <span style={{ fontWeight: 400, color: '#888' }}>(optional — brand, size, condition, age…)</span>
        </label>
        <textarea
          className="textarea"
          rows={3}
          value={notes}
          onChange={e => setNotes(e.target.value)}
          placeholder="e.g. Aero Leather Co. horsehide jacket, men's size 40, made in Scotland, excellent condition"
        />

        {error && <div className="error-box">{error}</div>}

        <div className="btn-row">
          <button className="btn primary" onClick={generateListing} disabled={loading || !imageBase64}>
            {loading ? <><span className="spinner" /> Analyzing…</> : '✨ Generate listing with AI'}
          </button>
        </div>

        {listing && (
          <div className="result-box">
            <label className="field-label">Title</label>
            <input
              className="edit-input"
              value={editTitle}
              onChange={e => setEditTitle(e.target.value)}
            />

            <label className="field-label" style={{ marginTop: '0.85rem' }}>Description</label>
            <textarea
              className="textarea"
              rows={4}
              value={editDescription}
              onChange={e => setEditDescription(e.target.value)}
            />

            <label className="field-label" style={{ marginTop: '0.85rem' }}>
              Search tags <span style={{ fontWeight: 400, color: '#888' }}>(comma-separated — edit freely)</span>
            </label>
            <textarea
              className="textarea"
              rows={2}
              value={editTags}
              onChange={e => setEditTags(e.target.value)}
            />

            <div className="value-chip">
              Estimated value: ${listing.estimatedValueLow}–${listing.estimatedValueHigh}
            </div>

            <div className="price-row">
              <label className="field-label" style={{ margin: 0 }}>Your asking price</label>
              <div className="price-input-wrap">
                <span className="price-dollar">$</span>
                <input
                  type="number"
                  className="price-input"
                  value={askingPrice}
                  min={0}
                  step={0.5}
                  onChange={e => setAskingPrice(e.target.value)}
                />
              </div>
            </div>

            <div className="btn-row" style={{ marginTop: '1rem' }}>
              <button className="btn primary" onClick={publish} disabled={publishing}>
                {publishing ? <><span className="spinner" /> Saving…</> : 'Publish to storefront →'}
              </button>
              <button className="btn secondary" onClick={generateListing} disabled={loading}>
                Regenerate
              </button>
            </div>
          </div>
        )}
      </div>

      {items.length > 0 && (
        <div className="published-section">
          <h2 className="section-title">Published items ({items.length})</h2>
          <div className="admin-grid">
            {items.map(item => (
              <div key={item.id} className="admin-card">
                {item.image_url && (
                  <img src={item.image_url} alt={item.title} className="admin-card-img" />
                )}
                <div className="admin-card-body">
                  <div className="admin-card-title">{item.title}</div>
                  <div className="admin-card-desc">{item.description}</div>
                </div>
                <div className="admin-card-footer">
                  <span className="card-price">${Number(item.price).toFixed(2)}</span>
                  <span className={`status-badge status-${item.status}`}>{item.status}</span>
                  <button className="remove-btn" onClick={() => onRemove(item.id)}>✕ Remove</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
